import { useEffect, createContext } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Button,
  Modal,
  Space,
  Spin,
  Card,
  Row,
  Col,
  Flex,
  Dropdown,
  MenuProps,
} from "antd";

import { DownOutlined } from "@ant-design/icons";

import ObjectForm from "components/Admin/ObjectForm";

import { RootState } from "Redux/store";
import { fetchUserAction, createUserAction, updateUserAction, deleteUserAction } from 'Redux/Admin/GoalsManager/UserActions/action';

import { settings, initialSettings, initialValues } from './const';
import { ADMIN_PATH } from "utils/routes";

import type { IUserAction } from 'Redux/Admin/GoalsManager/UserActions/type';

const UserAction = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { id } = useParams<{ id: string }>();
  const isNew = id === 'new';

  const { entities, loading, error } = useSelector((state: RootState) => state.fetchUserActions);
  const [modal, contextHolder] = Modal.useModal();

  const ReachableContext = createContext<string | null>(null);

  useEffect(() => {
    if (id && !isNew) {
      dispatch(fetchUserAction(id));
    }
  }, [dispatch, isNew, id]);

  const onFinish = (values: any) => {
    if (isNew) {
      dispatch(createUserAction(values, handleCreate));
    }
    else if (id) {
      dispatch(updateUserAction(values, id));
    }
  };

  const handleCreate = (userAction: IUserAction) => {
    if (userAction && userAction.id) {
      navigate(`${ADMIN_PATH}/goals/journeys/user-actions${userAction.id}`);
    }
  }

  if (!id) {
    navigate(`${ADMIN_PATH}/goals/journeys/user-actions`, { replace: true });
    return null;
  }

  const userAction = isNew ? initialValues : entities[id];

  if (!userAction) {
    return <Spin />;
  }

  const menuItems: MenuProps['items'] = [
    {
      label: 'Delete Action',
      key: '1',
      danger: true,
    }
  ];

  const modalConfig = {
    title: 'Delete the Action?',
    content: <span>Are you sure you want to delete the action <b>{userAction.id}</b>?</span>,
    confirmLoading: true
  };

  const handleMenuClick: MenuProps['onClick'] = async (e) => {
    if (e.key === '1') {
      const confirmed = await modal.confirm(modalConfig);

      if (confirmed) {
        dispatch(deleteUserAction(id, () => {
          navigate(`${ADMIN_PATH}/goals/journeys/user-actions`, { replace: true });
        }));
      }
    }
  };

  const menuProps = {
    items: menuItems,
    onClick: handleMenuClick,
  };

  return (
    <ReachableContext.Provider value="Light">
      <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <Row>
          <Col span={12}>
            <Flex
              gap="middle"
              align="center"
              justify="space-between"
              style={{ marginBottom: 16 }}
            >
              <Link to={`${ADMIN_PATH}/goals/journeys/user-actions`}>Back</Link>
            </Flex>

            <Card title={`${isNew ? 'Create' : '' } User Action`} extra={
              isNew ? null : <Dropdown menu={menuProps} placement="bottom">
                <Button>
                  <Space>
                    Manage Action
                    <DownOutlined />
                  </Space>
                </Button>
              </Dropdown>
            }>
              <ObjectForm
                data={userAction}
                errorData={error}
                isNew={isNew}
                settings={isNew ? initialSettings : settings}
                loading={loading}
                onFinish={onFinish}
              />
            </Card>
          </Col>
        </Row>
      </div>

      {contextHolder}
    </ReachableContext.Provider>
  );
}

export default UserAction;
